import React, { useEffect, useState } from "react";
import { Loader2, X, ArrowRight, Trash2, CheckCircle2 } from "lucide-react";
import { Button } from "../ui/button";
import { adminApi } from "../../lib/api";
import { useToast } from "../../hooks/use-toast";

const input = "w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm text-navy focus:outline-none focus:border-orange";

// One answer at a time, with the decisions that actually get taken during a
// review: the wording, the subject, whether it earns a page, and whether Google
// should see it. Everything else stays in the full editor.
export default function AskCassieReviewQueue({ queue, options, onClose, onFinished }) {
  const { toast } = useToast();
  const [i, setI] = useState(0);
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState({ approved: 0, saved: 0, parked: 0, deleted: 0 });

  const current = queue[i];
  useEffect(() => {
    setF(current ? { ...current } : null);
  }, [current]);

  const finish = () => {
    onFinished(done);
    onClose();
  };

  const next = (key) => {
    setDone((d) => ({ ...d, [key]: d[key] + 1 }));
    if (i + 1 >= queue.length) {
      onFinished({ ...done, [key]: done[key] + 1 });
      onClose();
      return;
    }
    setI(i + 1);
  };

  const save = async () => {
    const saved = await adminApi.askCassie.update(current.id, f);
    return saved;
  };

  const act = async (mode) => {
    setBusy(true);
    try {
      if (mode === "delete") {
        await adminApi.askCassie.remove(current.id);
        next("deleted");
        return;
      }
      await save();
      if (mode === "approve") {
        await adminApi.askCassie.approve(current.id, { publish: true });
        next("approved");
      } else if (mode === "park") {
        await adminApi.askCassie.assignCategory(current.id, options.holding.key);
        next("parked");
      } else {
        next("saved");
      }
    } catch (e) {
      toast({
        title: "Couldn't do that one",
        description: e?.response?.data?.detail || "Check the wording and the subject.",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  if (!f) return null;
  const set = (patch) => setF((v) => ({ ...v, ...patch }));

  return (
    <div className="fixed inset-0 z-50 bg-navy/50 backdrop-blur-sm overflow-y-auto p-4" data-testid="ask-cassie-queue">
      <div className="bg-white rounded-2xl w-full max-w-3xl mx-auto my-8 shadow-2xl">
        <div className="flex items-center justify-between gap-4 p-5 border-b border-slate-100 sticky top-0 bg-white rounded-t-2xl">
          <div>
            <h3 className="font-display font-bold text-navy">Reviewing the library</h3>
            <p className="text-xs text-slate-500 mt-0.5" data-testid="queue-progress">
              {i + 1} of {queue.length} · {done.approved} approved so far
            </p>
          </div>
          <button onClick={finish} data-testid="queue-close" className="text-slate-400 hover:text-navy"><X size={20} /></button>
        </div>

        <div className="h-1 bg-slate-100">
          <div className="h-1 bg-orange transition-all" style={{ width: `${((i + 1) / queue.length) * 100}%` }} />
        </div>

        <div className="p-6">
          {(f.appears_on || f.old_category) && (
            <p className="text-xs text-slate-400 mb-4" data-testid="queue-provenance">
              Brought across from {f.appears_on || "the old FAQ list"}
              {f.old_category ? ` · was filed under "${f.old_category}"` : ""}
            </p>
          )}

          <label className="block mb-4">
            <span className="text-xs font-bold uppercase tracking-wide text-slate-500">The question</span>
            <input className={`${input} mt-2`} value={f.question || ""} onChange={(e) => set({ question: e.target.value })} data-testid="queue-question" />
          </label>

          <label className="block mb-4">
            <span className="text-xs font-bold uppercase tracking-wide text-slate-500">The answer</span>
            <span className="block text-xs text-slate-400 mt-0.5">Two to four sentences. Read it as a stranger would: is it still true, and does it still sound like us?</span>
            <textarea rows={7} className={`${input} mt-2`} value={f.quick_answer || ""} onChange={(e) => set({ quick_answer: e.target.value })} data-testid="queue-answer" />
          </label>

          {f.level === "detailed" && (f.sections || []).length > 0 && (
            <div className="mb-6 rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="queue-sections">
              <p className="text-xs font-bold uppercase tracking-wide text-slate-500">The detail on the page</p>
              <p className="text-xs text-slate-400 mt-0.5">Read it here; edit it in the full editor.</p>
              <div className="mt-3 space-y-3">
                {(f.sections || []).map((s, k) => (
                  <div key={k}>
                    <p className="text-sm font-bold text-navy">{s.heading}</p>
                    <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-line mt-1">{s.body}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid sm:grid-cols-3 gap-3 mb-6">
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-wide text-slate-500">Subject</span>              <select className={`${input} mt-2`} value={f.category || ""} onChange={(e) => set({ category: e.target.value })} data-testid="queue-category">
                {(options.categories || []).map((c) => <option key={c.key} value={c.key}>{c.name}</option>)}
                {options.holding && <option value={options.holding.key}>{options.holding.name}</option>}
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-wide text-slate-500">Kind</span>
              <select className={`${input} mt-2`} value={f.level || "quick"} onChange={(e) => set({ level: e.target.value })} data-testid="queue-level">
                <option value="quick">Quick answer</option>
                <option value="detailed">Full page</option>
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-wide text-slate-500">Google</span>
              <select
                className={`${input} mt-2`}
                value={f.indexable === true ? "yes" : f.indexable === false ? "no" : ""}
                onChange={(e) => set({ indexable: e.target.value === "" ? null : e.target.value === "yes" })}
                data-testid="queue-indexable"
              >
                <option value="">Default for the subject</option>
                <option value="yes">Index it</option>
                <option value="no">Keep it out</option>
              </select>
            </label>
          </div>

          <div className="flex flex-wrap items-center gap-2.5">
            <Button onClick={() => act("approve")} disabled={busy} className="bg-teal hover:bg-teal/90 text-white rounded-full" data-testid="queue-approve">
              {busy ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <CheckCircle2 size={15} className="mr-1.5" />}
              Approve &amp; next
            </Button>
            <Button onClick={() => act("draft")} disabled={busy} variant="outline" className="rounded-full" data-testid="queue-save">
              Save as draft &amp; next
            </Button>
            <Button onClick={() => next("saved")} disabled={busy} variant="ghost" className="rounded-full text-slate-500" data-testid="queue-skip">
              Skip <ArrowRight size={14} className="ml-1.5" />
            </Button>
            <div className="flex-1" />
            {f.category !== options.holding?.key && (
              <button onClick={() => act("park")} disabled={busy} className="text-xs font-bold text-slate-500 hover:text-orange-dark" data-testid="queue-park">
                Needs a subject
              </button>
            )}
            <button onClick={() => act("delete")} disabled={busy} className="text-red-400 hover:text-red-600" title="Delete this one" data-testid="queue-delete">
              <Trash2 size={16} />
            </button>
          </div>

          <p className="text-[11px] text-slate-400 mt-5">
            Approving stamps today as the review date and, once the library is launched, lets Google see it. Nothing is
            published in anyone's name until you press Approve.
          </p>
        </div>
      </div>
    </div>
  );
}
